import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { useParams } from "react-router-dom";
import AuthService from "../../utils/AuthService";
import GameServer from "./GameServer";
import PlayerTable from "./PlayerTable";
import axios from "axios";
import ApiMap from "../../utils/ApiMap";
import { Box, CircularProgress, Typography } from "@mui/material";

function DisplayActivity({
  duration,
  startedon,
  participants,
  waiting,
  offline,
  gamename,
  totalQuestions,
  modulename,
  modulecid,
  courseid,
  quizcid,
  gamecode,
  activityLog,
  onPlayerRemoved,
}) {
  const hasLobbyData =
    (participants && participants.length > 0) ||
    (waiting && waiting.length > 0) ||
    (offline && offline.length > 0);

  if (!hasLobbyData) return null;

  return (
    <PlayerTable
      duration={duration}
      startedon={startedon}
      players={participants}
      waitingPlayers={waiting}
      offlinePlayers={offline}
      gamename={gamename}
      totalquestions={totalQuestions}
      modulename={modulename}
      modulecid={modulecid}
      courseid={courseid}
      quizcid={quizcid}
      gamecode={gamecode}
      activityLog={activityLog}
      onPlayerRemoved={onPlayerRemoved}
    />
  );
}

function WatchGame({ setShowAppBar }) {
  const { gameCode } = useParams();
  const gamecode = gameCode;

  // 🔹 State
  const [activityLog, setActivityLog] = useState([]);
  const [gameRoomUsers, setGameRoomUsers] = useState([]);
  const [waitingRoomUsers, setWaitingRoomUsers] = useState([]);
  const [offlineRoomUsers, setOfflineRoomUsers] = useState([]);
  const [duration, setDuration] = useState();
  const [startedOn, setStartedOn] = useState();
  const [gameName, setGameName] = useState();
  const [moduleName, setModuleName] = useState();
  const [moduleCid, setModuleCid] = useState();
  const [courseId, setCourseId] = useState();
  const [quizCid, setQuizCid] = useState();
  const [totalQuestions, setTotalQuestions] = useState();
  const [loading, setLoading] = useState(true);

  // 🔹 Hide AppBar
  useLayoutEffect(() => {
    setShowAppBar?.(false);
    return () => setShowAppBar?.(true);
  }, [setShowAppBar]);

  // 🔹 Fetch game info
  useEffect(() => {
    const fetchGameTime = async () => {
      try {
        const url = `${ApiMap.GAME_INFO}/${gamecode}`;
        const resp = await axios(url, AuthService.getAuthHeader());
        const data = resp.data;

        setDuration(data.DurationMin);
        setStartedOn(data.StartedOn);
        setGameName(data.Name);
        setModuleName(data.ModuleName);
        setModuleCid(data.ModuleCid);
        setCourseId(data.CourseId);
        setQuizCid(data.QuizCid);

        const totalItemsCandidate =
          data.ItemCount ??
          data.TotalQuestions ??
          data.TotalItems ??
          data.QuestionCount ??
          null;
        const parsed = Number(totalItemsCandidate);
        setTotalQuestions(Number.isFinite(parsed) ? parsed : null);
      } catch (err) {
        console.error("❌ Failed to load game info", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameTime();
  }, [gamecode]);

  // 🔹 Utility helpers
  const normalizeToArray = useCallback(
    (value) => (Array.isArray(value) ? value : value ? [value] : []),
    []
  );

  const extractUserId = useCallback((value) => {
    if (!value) return null;
    const match = String(value).match(/user#(.+)/i);
    return match?.[1] ?? String(value);
  }, []);

  const extractItemId = useCallback((value) => {
    if (!value) return null;
    const str = String(value);
    const match = str.match(/itemId#([^;]+)/i);
    if (match?.[1]) return match[1];
    if (str.includes(";")) return str.split(";")[0];
    return str;
  }, []);

  // 🔹 Build player activity stats
  const buildActivityIndex = useCallback(
    (activityList = []) => {
      const index = new Map();
      const safe = Array.isArray(activityList) ? activityList : [];

      safe.forEach((entry) => {
        if (!entry) return;
        const userKey =
          entry.PK ??
          entry.pk ??
          entry.UserId ??
          entry.UserID ??
          entry.MemberId ??
          entry.MemberID;
        const userId = extractUserId(userKey);
        if (!userId) return;

        const itemKey =
          entry.SK ??
          entry.sk ??
          entry.ItemId ??
          entry.ItemID ??
          entry.ActivityId ??
          entry.ActivityID;
        const itemId = extractItemId(itemKey);

        const answeredCorrect =
          entry.answeredCorrect === true || entry.AnsweredCorrect === true;
        const attemptsCount =
          Number(entry.attempts ?? entry.Attempts ?? 0) || 0;

        const responseMs = Number(
          entry.responseTimeInMillsec ??
            entry.ResponseTimeInMillsec ??
            entry.responseTimeInMillis ??
            entry.ResponseTime ??
            entry.ResponseTimeMs ??
            entry.ResponseTimeMilliseconds ??
            0
        );
        const avgResponseMs = Number(
          entry.avgRespTimeInMillsec ??
            entry.AvgRespTimeInMillsec ??
            entry.avgResponseTime ??
            entry.AvgRespTime ??
            0
        );
        const lastAttemptTime =
          entry.lastAttemptTime ??
          entry.LastAttemptTime ??
          entry.RecentSubmitDate ??
          null;

        const summary = index.get(userId) || {
          userId,
          items: new Map(),
          correctCount: 0,
          totalAttempts: 0,
          totalResponseMs: 0,
          responseEntryCount: 0,
          lastAttemptTime: null,
          profile: {},
        };

        summary.totalAttempts += attemptsCount;
        if (answeredCorrect) summary.correctCount++;

        if (Number.isFinite(avgResponseMs) && avgResponseMs > 0) {
          summary.totalResponseMs += avgResponseMs;
          summary.responseEntryCount++;
        } else if (Number.isFinite(responseMs) && responseMs > 0) {
          summary.totalResponseMs += responseMs;
          summary.responseEntryCount++;
        }

        if (
          lastAttemptTime &&
          (!summary.lastAttemptTime ||
            Date.parse(lastAttemptTime) > Date.parse(summary.lastAttemptTime))
        ) {
          summary.lastAttemptTime = lastAttemptTime;
        }

        index.set(userId, summary);
      });

      return index;
    },
    [extractItemId, extractUserId]
  );

  const activityIndex = useMemo(
    () => buildActivityIndex(activityLog),
    [activityLog, buildActivityIndex]
  );

  const coerceNumber = useCallback((value) => {
    if (value == null || value === "") return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const parsed = Number(value.replace(/[^0-9.+-]/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, []);

  // 🔹 Enhance users
  const enhanceUsers = useCallback(
    (users = [], { isWaiting = false, isOffline = false } = {}) => {
      const safe = Array.isArray(users) ? users : [];
      const totalQuestionCount = coerceNumber(totalQuestions);
      const hasValidTotal =
        Number.isFinite(totalQuestionCount) && totalQuestionCount > 0;

      return safe.map((user, index) => {
        const userId = extractUserId(
          user.UserId ??
            user.UserID ??
            user.MemberId ??
            user.MemberID ??
            user.Id ??
            user.ID ??
            user.Email ??
            user.email ??
            user.DisplayName ??
            user.displayName ??
            user.Name ??
            user.name ??
            `user-${index}`
        );

        const summary = userId ? activityIndex.get(userId) : undefined;
        const profile = summary?.profile ?? {};

        const firstName =
          user.FirstName ??
          profile.firstName ??
          user.Name ??
          user.DisplayName ??
          "";
        const lastName =
          user.LastName ?? profile.lastName ?? user.Surname ?? user.Last ?? "";
        const displayName = [firstName, lastName]
          .filter(Boolean)
          .join(" ")
          .trim();

        const joinedOn =
          user.JoinedOn ??
          user.Joined ??
          user.CreatedOn ??
          profile.joinedOn ??
          null;

        const itemsCompleted =
          summary?.items?.size ??
          coerceNumber(user.ItemsCompleted ?? user.CompletedItems ?? 0);

        const scoreValue =
          summary?.correctCount ??
          coerceNumber(user.Score ?? user.Points ?? user.TotalScore ?? 0);

        const percentCompleted =
          hasValidTotal && itemsCompleted != null
            ? (itemsCompleted / totalQuestionCount) * 100
            : null;

        return {
          ...user,
          UserId: user.UserId ?? userId ?? user.Email ?? user.Id,
          MemberId: user.MemberId ?? userId ?? user.Email ?? user.Id,
          FirstName: firstName,
          LastName: lastName,
          DisplayName: displayName,
          FullName: displayName,
          JoinedOn: joinedOn,
          LastReportedOn:
            summary?.lastAttemptTime ??
            user.LastReportedOn ??
            user.LastSeen ??
            profile.lastReportedOn ??
            null,
          ItemsCompleted: itemsCompleted,
          ItemCount: hasValidTotal
            ? totalQuestionCount
            : coerceNumber(
                user.ItemCount ??
                  user.ItemsTotal ??
                  user.TotalItems ??
                  user.Total ??
                  null
              ),
          PercentCompleted:
            percentCompleted != null ? percentCompleted : undefined,
          Score: scoreValue != null ? scoreValue : undefined,
          IsWaiting: isWaiting ? true : user.IsWaiting,
          InWaitingRoomFlag: isWaiting ? true : user.InWaitingRoomFlag,
          IsOffline: isOffline ? true : user.IsOffline,
        };
      });
    },
    [activityIndex, coerceNumber, extractUserId, totalQuestions]
  );

  const participants = useMemo(
    () => enhanceUsers(gameRoomUsers),
    [gameRoomUsers, enhanceUsers]
  );
  const waitingParticipants = useMemo(
    () => enhanceUsers(waitingRoomUsers, { isWaiting: true }),
    [waitingRoomUsers, enhanceUsers]
  );
  const offlineParticipants = useMemo(() => {
    const activeIds = new Set(
      [...gameRoomUsers, ...waitingRoomUsers].map((u) =>
        extractUserId(u.UserId ?? u.MemberId ?? u.Email ?? u.Name)
      )
    );

    const offlineMap = new Map();
    offlineRoomUsers.forEach((u) => {
      const id = extractUserId(u.UserId ?? u.MemberId ?? u.Email ?? u.Name);
      if (!id || activeIds.has(id)) return;
      offlineMap.set(id, u);
    });

    activityIndex.forEach((summary, uid) => {
      if (!activeIds.has(uid) && !offlineMap.has(uid)) {
        offlineMap.set(uid, {
          UserId: uid,
          FirstName: summary.profile?.firstName ?? "",
          LastReportedOn: summary.lastAttemptTime ?? null,
        });
      }
    });

    return enhanceUsers(Array.from(offlineMap.values()), { isOffline: true });
  }, [
    activityIndex,
    enhanceUsers,
    extractUserId,
    gameRoomUsers,
    waitingRoomUsers,
    offlineRoomUsers,
  ]);

  // 🔹 Handle WebSocket events
  const handleDisplayActivityMessage = useCallback(
    (msg) => {
      if (!msg) return;
      console.log("📩 msgdisplayactivity:", msg);

      let root = msg.message ?? msg.Message ?? msg;
      if (typeof root === "string") {
        try {
          root = JSON.parse(root);
        } catch {}
      }

      if (root.Activity) setActivityLog(normalizeToArray(root.Activity));
      if (root.usersInGameRoom)
        setGameRoomUsers(normalizeToArray(root.usersInGameRoom));
      if (root.UsersInGameRoom)
        setGameRoomUsers(normalizeToArray(root.UsersInGameRoom));
      if (root.usersInWaitingRoom)
        setWaitingRoomUsers(normalizeToArray(root.usersInWaitingRoom));
      if (root.UsersInWaitingRoom)
        setWaitingRoomUsers(normalizeToArray(root.UsersInWaitingRoom));
      if (root.offlineUsers)
        setOfflineRoomUsers(normalizeToArray(root.offlineUsers));
      if (root.OfflineUsers)
        setOfflineRoomUsers(normalizeToArray(root.OfflineUsers));
    },
    [normalizeToArray]
  );

  const handleJoinedMessage = useCallback(
    (msg) => {
      const payload = msg?.message ?? msg?.Message ?? msg;
      const waiting =
        payload?.usersInWaitingRoom ?? payload?.UsersInWaitingRoom;
      if (waiting) setWaitingRoomUsers(normalizeToArray(waiting));
    },
    [normalizeToArray]
  );

  const handlePlayerRemoved = (player) => {
    setGameRoomUsers((prev) =>
      prev.filter(
        (p) =>
          p.UserId !== player.UserId &&
          p.MemberId !== player.MemberId &&
          p.Email !== player.Email
      )
    );
  };

  // 🔹 Fetch current connected players
  const getConnectedPlayers = useCallback(async () => {
    try {
      const url = `${ApiMap.GET_GAMECODE}/gameplayers/${gamecode}`;
      const resp = await axios(url, AuthService.getAuthHeader());
      const data = resp?.data ?? {};

      if (Array.isArray(data.players)) {
        setGameRoomUsers(data.players);
      } else {
        handleDisplayActivityMessage(data);
      }
    } catch (err) {
      console.error("❌ Failed to fetch connected players", err);
    }
  }, [gamecode, handleDisplayActivityMessage]);

  // 🔹 Start monitoring (classic way)
  const startMonitor = useCallback(() => {
    console.log("🚀 Starting Game Watch Monitor for:", gamecode);
    GameServer.notify(
      gamecode,
      "msgwatchgame",
      { Code: gamecode },
      {
        msgdisplayactivity: handleDisplayActivityMessage,
        joined: handleJoinedMessage,
      }
    );
  }, [gamecode, handleDisplayActivityMessage, handleJoinedMessage]);

  // 🔹 Run once and fetch players immediately
  const startedRef = useRef(false);
  if (!startedRef.current) {
    startMonitor();
    startedRef.current = true;
  }

  useEffect(() => {
    getConnectedPlayers();
  }, [getConnectedPlayers]);

  return (
    <Box
      minHeight="100vh"
      bgcolor="#f6f8ff"
      color="#1f2933"
      py={4}
      px={{ xs: 2, md: 4 }}
    >
      {participants.length > 0 ||
      waitingParticipants.length > 0 ||
      offlineParticipants.length > 0 ? (
        <DisplayActivity
          duration={duration}
          startedon={startedOn}
          participants={participants}
          waiting={waitingParticipants}
          offline={offlineParticipants}
          gamename={gameName}
          modulename={moduleName}
          modulecid={moduleCid}
          courseid={courseId}
          quizcid={quizCid}
          totalQuestions={totalQuestions}
          gamecode={gamecode}
          activityLog={activityLog}
          onPlayerRemoved={handlePlayerRemoved}
        />
      ) : (
        <Box textAlign="center" color="#1f2933">
          <CircularProgress />
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            {loading ? "Syncing game lobby…" : "Waiting for live activity"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default WatchGame;
